import pluralize from "@theothergothamdev/pluralize-ts";
import Fraction from 'fraction.js';

const descriptorsToRemove = [
    'sliced', 'diced', 'chopped', 'minced', 'crushed', 'shredded',
    'peeled', 'grated', 'mashed', 'halved', 'whole', 'fresh', 'dried',
    'frozen', 'cooked', 'uncooked', 'raw', 'rinsed', 'blanched',
    'beaten', 'boiled', 'ground', 'julienned', 'pureed', 'trimmed'
];

const PREP_WORDS = [
    'chopped', 'sliced', 'minced', 'diced', 'grated',
    'peeled', 'shredded', 'crushed', 'whole', 'halved',
    'quartered', 'julienned', 'mashed'
];

const normalizeItemNameV2 = (item: string) => {
    let cleaned = item.toLowerCase();

    // Remove words like sliced, chopped etc.
    PREP_WORDS.forEach(word => {
        const regex = new RegExp(`\\b${word}\\b`, 'gi');
        cleaned = cleaned.replace(regex, '');
    });

    cleaned = cleaned.replace(/[\(\)\-]/g, ''); // remove brackets and dashes
    cleaned = pluralize.singular(cleaned.trim());
    cleaned = cleaned.replace(/\s{2,}/g, ' '); // remove extra spaces
    return cleaned;
};

function toFraction(decimal: number): string {
    const gcd = (a: number, b: number): number => (b ? gcd(b, a % b) : a);
    const tolerance = 1.0E-6;
    let numerator = 1;
    let denominator = 1;

    while (Math.abs(numerator / denominator - decimal) > tolerance) {
        if (numerator / denominator < decimal) {
            numerator++;
        } else {
            denominator++;
            numerator = Math.round(decimal * denominator);
        }
    }

    const divisor = gcd(numerator, denominator);
    numerator /= divisor;
    denominator /= divisor;

    // console.log("Fraction: ", denominator === 1 ? `${numerator}` : `${numerator}/${denominator}`);

    return denominator === 1 ? `${numerator}` : `${numerator}/${denominator}`;
}

export function toFractionHTML(decimal: number): string {
    const fraction = new Fraction(decimal);

    // console.log("Converting: ", decimal, fraction.n, fraction.d);
    const whole = Math.floor(Number(fraction.n) / Number(fraction.d));
    const remainderNumerator = Number(fraction.n) % Number(fraction.d);
    const denominator = Number(fraction.d);

    const hasFractionalPart = Number(remainderNumerator) !== 0;

    // Construct HTML
    const fractionHTML = hasFractionalPart
        ? `<sup>${remainderNumerator}</sup>&frasl;<sub>${denominator}</sub>`
        : '';

    if (whole === 0 && hasFractionalPart) {
        return fractionHTML; // e.g. <sup>1</sup>&frasl;<sub>2</sub>
    } else if (whole !== 0 && hasFractionalPart) {
        return `${whole} ${fractionHTML}`; // e.g. 2 <sup>1</sup>&frasl;<sub>2</sub>
    } else {
        return `${whole}`; // whole number like 3
    }
}


export function toFractionHTMLv2(decimal: number): string {
    const gcd = (a: number, b: number): number => (b ? gcd(b, a % b) : a);
    const tolerance = 1.0E-6;

    const whole = Math.floor(decimal);
    const fractional = decimal - whole;

    if (fractional < tolerance) {
        return `${whole}`; // It's a whole number
    }

    // Convert fractional part to a fraction
    let bestNumerator = 1;
    let bestDenominator = 1;
    let minError = Math.abs(fractional - bestNumerator / bestDenominator);

    for (let d = 1; d <= 1000; d++) {
        const n = Math.round(fractional * d);
        const error = Math.abs(fractional - n / d);
        if (error < minError - tolerance) {
            bestNumerator = n;
            bestDenominator = d;
            minError = error;
        }
        if (minError < tolerance) break;
    }

    const divisor = gcd(bestNumerator, bestDenominator);
    const numerator = bestNumerator / divisor;
    const denominator = bestDenominator / divisor;

    const fractionHTML = `<sup>${numerator}</sup>&frasl;<sub>${denominator}</sub>`;

    if (whole === 0) {
        return fractionHTML;
    } else {
        return `${whole} ${fractionHTML}`;
    }
}

export function toFractionHTMLv1(decimal: number): string {
    const gcd = (a: number, b: number): number => (b ? gcd(b, a % b) : a);
    const tolerance = 1.0E-6;
    let numerator = 1;
    let denominator = 1;

    const whole = Math.floor(decimal);
    const fractional = decimal - whole;

    if (fractional < tolerance) {
        // It's a whole number
        return `${whole}`;
    }

    while (Math.abs(numerator / denominator - decimal) > tolerance) {
        if (numerator / denominator < decimal) {
            numerator++;
        } else {
            denominator++;
            numerator = Math.round(decimal * denominator);
        }
    }

    const divisor = gcd(numerator, denominator);
    numerator /= divisor;
    denominator /= divisor;

    let fractionHTML1 = `<sup>${numerator}</sup>&frasl;<sub>${denominator}</sub>`;
    let fractionHTML2 = `<sup>${numerator - (whole * denominator)}</sup>&frasl;<sub>${denominator}</sub>`;

    // Return fraction with numerator as <sup> and denominator as <sub>
    // e.g. 21/2 → "<sup>21</sup>&frasl;<sub>2</sub>"
    if (whole === 0) {
        return fractionHTML1;
    } else {
        return `${whole} ${fractionHTML2}`;
    }
    // if (denominator === 1) {
    //   // If whole number, just return numerator as string
    //   return `${numerator}`;
    // } else {
    //   return `<sup>${numerator}</sup>&frasl;<sub>${denominator}</sub>`;
    // }
}

function normalizeItemName(name: string): string {
    return name
        .toLowerCase()
        .replace(/\(.*?\)/g, '') // remove things in brackets
        // .replace(/\b(sliced|diced|chopped|drained|fresh|frozen|cooked|raw|peeled|minced|crushed|whole|grated|ground|shredded|halved|quartered|mashed|boiled|baked|roasted|steamed|beaten|blended|to taste)\b/g, '')
        // .replace(/[^a-zA-Z\s]/g, '') // remove non-letter chars
        .replace(/\s+/g, ' ') // normalize whitespace
        .trim();
}

const unitWhitelist = ['g', 'kg', 'oz', 'ml', 'tbsp', 'tsp', 'cups', 'cup', 'l', 'lb'];

function extractUnit(input: string) {
    const match = input?.match(/-\s*([\d.]+\s*(tbsp|g|tsp|spoon|ml|oz|cups|cup|l|lb|tins|slices|tin|slice))/);
    if (!match) {
        const matchAlt = input?.match(/([\d.]+\s*(tbsp|g|tsp|spoon|ml|oz|cups|cup|l|lb|tins|tin|slices|slice))/);
        return matchAlt ? matchAlt[1] : ""
    }
    return match ? match[1] : "";
}

function extractNumericValue(input: string) {
    let match = input?.match(/(\d+(\.\d+)?)/);
    if (match) {
        return match ? parseFloat(match[0]) : 0;
    }
    match = input?.match(/-\s*(\b[\d.]+\b)(?![a-zA-Z])/);
    if (!match) {
        const matchAlt = input?.match(/(\b[\d.]+\b)(?![a-zA-Z])/);
        return matchAlt ? parseFloat(matchAlt[1]) : 0;
    }
    return match ? parseFloat(match[1]) : 0;
}

function extractMetric(value: string): string {
    const match = value?.match(/[a-zA-Z]+$/);
    return match ? match[0].toLowerCase() : '';
}

export function parseIngredient(ingredient: string): any {
    // Format A: "100g butter beans (drained)"
    const regexes = [
        { regex: /^(.+?)\s*[-–—]\s*(\d+(?:\.\d+)?[a-zA-Z]+)$/, type: 1 },
        { regex: /^(.+?)\s*[-–—]\s*(\d+(?:\.\d+)?)$/, type: 2 },
        { regex: /^(.+?)(?:\s+(\d+.*))?$/, type: 3 },
        { regex: /^([^ ]+)\s+(.+)$/, type: 4 },
        { regex: /^(\d*\.?\d+)?\s*([a-zA-Z]+)?\s*(.+)$/, type: 5 },
        { regex: /^(.+?)\s*[-:]\s*(\d*\.?\d+)\s*([a-zA-Z]+)?$/, type: 6 }
    ];

    let quantity = 1;
    let unit = '';
    let item = '';
    let rawItem = '';
    let normalizedName = '';
    let multiplier = 1;
    let metric = "";
    let unitWeight = 1;
    let altQuantity = 1;

    for (const { regex, type } of regexes) {
        let match
        match = ingredient.match(regex);

        if (!match) {
            continue;
        }

        const [, itemRaw, unitRaw] = match;
        quantity = 1;
        altQuantity = 1;
        unit = extractUnit(unitRaw);
        metric = extractMetric(unit);
        unitWeight = isNaN(parseFloat(unit)) ? NaN : parseFloat(unit);
        item = normalizeItemName(itemRaw.trim());
        rawItem = itemRaw.trim();
        normalizedName = item.toLowerCase();
        multiplier = extractNumericValue(unitRaw);
        if (multiplier > 0) {
            quantity *= multiplier;
            altQuantity = isNaN(unitWeight) || unitWeight === 1
                ? multiplier
                : multiplier !== unitWeight ? multiplier * unitWeight : unitWeight;
        } else {
            altQuantity = 1;
        }

        // console.log({
        //     ingredient,
        //     matchType: type,
        //     unitRaw,
        //     unit,
        //     multiplier,
        //     unitWeight,
        //     altQuantity
        // });

        return {
            quantity,
            unit,
            item,
            itemRaw,
            original: [ingredient],
            matchType: type,
            metric,
            altQuantity,
            quantityFraction: toFractionHTML(altQuantity)
        }
    }
    // Fallback
    item = normalizeItemName(ingredient);
    unit = '';
    return {
        quantity,
        unit,
        item,
        itemRaw: ingredient,
        original: [ingredient],
        matchType: "no match",
        metric,
        altQuantity,
        quantityFraction: toFractionHTML(quantity)
    };
}